import { useState, type ReactNode } from 'react'

import { useProjects } from '@/hooks/useProjects'
import { useCreateProject, usePatchProject, useDeleteProject } from '@/hooks/useMutations'
import { Loading } from '@/components/ui/Loading'
import { ErrorState } from '@/components/ui/ErrorState'
import { EmptyState } from '@/components/ui/EmptyState'
import { Modal } from '@/components/ui/Modal'
import { t } from '@/lib/i18n'
import type { Project, ProjectStatus, ProjectType, Platform } from '@/types/api'
import styles from './ProjectsPage.module.css'

const STATUS_LABELS: Record<ProjectStatus, string> = {
  active: '활성',
  idea: '아이디어',
  maintenance: '유지보수',
  sunset: '종료 예정',
  archived: '보관됨',
}

type Filter = 'all' | ProjectStatus

const FILTERS: { key: Filter; label: string }[] = [
  { key: 'all', label: '전체' },
  { key: 'active', label: '🟢 활성' },
  { key: 'idea', label: '💡 아이디어' },
]

function PlatformBadge({ platform }: { platform: Platform }) {
  const label = platform === 'cloudflare' ? 'Cloudflare' : platform === 'local' ? 'Local' : platform
  const cls = platform === 'cloudflare' ? styles.badgeCloudflare : styles.badgeLocal
  return <span className={`${styles.badge} ${cls}`}>{label}</span>
}

function ProjectCard({
  project,
  onEdit,
  onDelete,
}: {
  project: Project
  onEdit: () => void
  onDelete: () => void
}) {
  const isIdea = project.status === 'idea'
  const dotClass =
    project.status === 'active' ? styles.dotActive
    : project.status === 'idea' ? styles.dotIdea
    : project.status === 'maintenance' ? styles.dotMaintenance
    : project.status === 'sunset' ? styles.dotSunset
    : styles.dotArchived

  return (
    <div className={`${styles.card} ${isIdea ? styles.cardIdea : ''}`}>
      <div className={styles.cardHeader}>
        <span className={`${styles.statusDot} ${dotClass}`} />
        <span className={styles.cardName}>{project.name}</span>
      </div>
      {project.description && <p className={styles.cardDesc}>{project.description}</p>}
      <div className={styles.meta}>
        <PlatformBadge platform={project.platform} />
        <span className={`${styles.badge} ${styles.badgeType}`}>{project.type}</span>
        {project.startDate && (
          <span style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>{project.startDate}부터</span>
        )}
      </div>
      <div className={styles.netBox}>
        <span className={styles.netLabel}>{STATUS_LABELS[project.status]}</span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 'var(--space-sm)' }}>
          <button
            type="button"
            onClick={onEdit}
            style={{
              background: 'none',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-sm)',
              padding: '2px 8px',
              fontSize: 12,
              cursor: 'pointer',
              color: 'var(--color-text-secondary)',
            }}
          >
            편집
          </button>
          <button
            type="button"
            onClick={onDelete}
            style={{
              background: 'none',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-sm)',
              padding: '2px 8px',
              fontSize: 12,
              cursor: 'pointer',
              color: 'var(--color-danger-text)',
            }}
          >
            삭제
          </button>
        </div>
      </div>
    </div>
  )
}

const inputStyle: React.CSSProperties = {
  width: '100%', height: 40, padding: '0 12px',
  border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)',
  fontSize: 14, outline: 'none', background: 'var(--color-surface)', color: 'var(--color-text)',
}
const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4, marginTop: 16,
}

const hintStyle: React.CSSProperties = {
  fontSize: 12, color: 'var(--color-text-tertiary)', marginTop: 4, lineHeight: 1.5,
}

export default function ProjectsPage() {
  const [filter, setFilter] = useState<Filter>('all')
  const [modalOpen, setModalOpen] = useState(false)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [editingProject, setEditingProject] = useState<Project | null>(null)
  const [editForm, setEditForm] = useState({
    name: '',
    description: '',
    status: 'active' as ProjectStatus,
    platform: 'cloudflare' as Platform,
    type: 'saas' as ProjectType,
  })
  const [form, setForm] = useState({
    name: '',
    slug: '',
    description: '',
    platform: 'cloudflare' as Platform,
    type: 'saas' as Project['type'],
  })

  const query = filter === 'all' ? {} : { status: filter }
  const { data, isLoading, error, refetch } = useProjects(query)
  const createProject = useCreateProject()
  const patchProject = usePatchProject()
  const deleteProject = useDeleteProject()

  function handleEditClick(project: Project) {
    setEditingProject(project)
    setEditForm({
      name: project.name,
      description: project.description ?? '',
      status: project.status,
      platform: project.platform,
      type: project.type,
    })
    setEditModalOpen(true)
  }

  function handleEditSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!editingProject) return
    patchProject.mutate(
      {
        id: editingProject.id,
        body: {
          name: editForm.name,
          description: editForm.description || undefined,
          status: editForm.status,
          platform: editForm.platform,
          type: editForm.type,
        },
      },
      {
        onSuccess: () => {
          setEditModalOpen(false)
          setEditingProject(null)
        },
      },
    )
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    createProject.mutate(form, {
      onSuccess: () => {
        setModalOpen(false)
        setForm({ name: '', slug: '', description: '', platform: 'cloudflare', type: 'saas' })
      },
    })
  }

  function slugify(name: string): string {
    return name.toLowerCase().trim().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
  }

  const headerAction: ReactNode = (
    <button
      type="button"
      onClick={() => setModalOpen(true)}
      className={styles.addButton}
    >
      + {t('Add project')}
    </button>
  )

  return (
    <>
      <div className={styles.filterBar}>
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => setFilter(f.key)}
            className={`${styles.filterChip} ${filter === f.key ? styles.filterChipActive : ''}`}
          >
            {f.label}
          </button>
        ))}
        {headerAction}
      </div>

      {isLoading && <Loading variant="cards" count={6} />}
      {error && <ErrorState error={error} onRetry={() => refetch()} />}
      {data && data.length === 0 && (
        <EmptyState
          icon={<span>⚓</span>}
          title="아직 프로젝트가 없습니다"
          description="첫 프로젝트를 추가해 운영을 시작하세요."
        />
      )}
      {data && data.length > 0 && (
        <div className={styles.grid}>
          {data.map((p) => (
            <ProjectCard
              key={p.id}
              project={p}
              onEdit={() => handleEditClick(p)}
              onDelete={() => {
                if (confirm(`"${p.name}" 프로젝트를 삭제하시겠습니까?`)) {
                  deleteProject.mutate(p.id)
                }
              }}
            />
          ))}
        </div>
      )}

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title="프로젝트 추가">
        <form onSubmit={handleSubmit}>
          <label style={labelStyle} htmlFor="p-name">{t('Name')}</label>
          <input
            id="p-name"
            style={inputStyle}
            type="text"
            placeholder="예: Vanko, Hellform, X-Auto"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value, slug: slugify(e.target.value) })}
            required
          />
          <p style={hintStyle}>{t('Display name shown in dashboard and project list')}</p>

          <label style={labelStyle} htmlFor="p-slug">{t('Slug (identifier)')}</label>
          <input
            id="p-slug"
            style={{ ...inputStyle, fontFamily: 'var(--font-mono)', fontSize: 13 }}
            type="text"
            placeholder="vanko"
            value={form.slug}
            onChange={(e) => setForm({ ...form, slug: slugify(e.target.value) })}
            required
          />
          <p style={hintStyle}>{t('Unique identifier for URL, API, ingest. Lowercase letters, numbers, hyphens only. Auto-generated from name.')}</p>

          <label style={labelStyle} htmlFor="p-desc">{t('Description')}</label>
          <input
            id="p-desc"
            style={inputStyle}
            type="text"
            placeholder="예: 해외 뉴스를 AI로 수집·요약하는 서비스"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
          <p style={hintStyle}>{t('One-line project description. Shown on cards (2 lines max).')}</p>

          <label style={labelStyle} htmlFor="p-platform">{t('Platform (hosting)')}</label>
          <select id="p-platform" style={inputStyle} value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value as Platform })}>
            <option value="cloudflare">Cloudflare — Workers/Pages/D1/R2</option>
            <option value="local">Local — 로컬 서버 (Pi 등, Quay가 직접 접근 불가)</option>
            <option value="mixed">Mixed — 클라우드+로컬 혼합</option>
            <option value="external">External — 외부 서드파티 호스팅</option>
          </select>
          <p style={hintStyle}>
            <strong>Cloudflare</strong>: Quay가 URL·API로 직접 모니터링·비용 수집 가능<br/>
            <strong>Local</strong>: X-Auto처럼 로컬에서 실행. Quay가 접근 불가 → heartbeat·ingest 푸시만 가능
          </p>

          <label style={labelStyle} htmlFor="p-type">{t('Type (project nature)')}</label>
          <select id="p-type" style={inputStyle} value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value as Project['type'] })}>
            <option value="saas">SaaS — 서비스형 소프트웨어 (고객이 사용)</option>
            <option value="tool">Tool — 도구·유틸리티 (개인/팀 사용)</option>
            <option value="internal">Internal — 내부 운영 도구 (Quay 자체 등)</option>
            <option value="library">Library — 라이브러리·패키지 (npm, PyPI)</option>
            <option value="marketing">Marketing — 홍보·랜딩 페이지</option>
          </select>
          <p style={hintStyle}>대시보드에서 타입별로 그룹화·필터링할 수 있습니다.</p>

          <button
            type="submit"
            disabled={createProject.isPending}
            style={{
              marginTop: 24, width: '100%', height: 44,
              background: 'var(--color-primary)', color: '#fff',
              borderRadius: 'var(--radius-sm)', fontWeight: 600, fontSize: 14,
              opacity: createProject.isPending ? 0.6 : 1,
            }}
          >
            {createProject.isPending ? t('Adding...') : t('Add project')}
          </button>
          {createProject.error && (
            <p style={{ marginTop: 8, fontSize: 13, color: 'var(--color-danger-text)' }}>
              {(createProject.error as Error).message}
            </p>
          )}
        </form>
      </Modal>

      <Modal open={editModalOpen} onClose={() => { setEditModalOpen(false); setEditingProject(null) }} title="프로젝트 편집">
        <form onSubmit={handleEditSubmit}>
          <label style={labelStyle} htmlFor="e-name">이름</label>
          <input
            id="e-name"
            style={inputStyle}
            type="text"
            value={editForm.name}
            onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
            required
          />

          <label style={labelStyle} htmlFor="e-desc">설명</label>
          <input
            id="e-desc"
            style={inputStyle}
            type="text"
            value={editForm.description}
            onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
          />

          <label style={labelStyle} htmlFor="e-status">상태</label>
          <select
            id="e-status"
            style={inputStyle}
            value={editForm.status}
            onChange={(e) => setEditForm({ ...editForm, status: e.target.value as ProjectStatus })}
          >
            <option value="active">활성</option>
            <option value="idea">아이디어</option>
            <option value="maintenance">유지보수</option>
            <option value="sunset">종료 예정</option>
            <option value="archived">보관됨</option>
          </select>

          <label style={labelStyle} htmlFor="e-platform">플랫폼</label>
          <select
            id="e-platform"
            style={inputStyle}
            value={editForm.platform}
            onChange={(e) => setEditForm({ ...editForm, platform: e.target.value as Platform })}
          >
            <option value="cloudflare">Cloudflare</option>
            <option value="local">Local</option>
            <option value="mixed">Mixed</option>
            <option value="external">External</option>
          </select>

          <label style={labelStyle} htmlFor="e-type">타입</label>
          <select
            id="e-type"
            style={inputStyle}
            value={editForm.type}
            onChange={(e) => setEditForm({ ...editForm, type: e.target.value as ProjectType })}
          >
            <option value="saas">SaaS</option>
            <option value="tool">Tool</option>
            <option value="internal">Internal</option>
            <option value="library">Library</option>
            <option value="marketing">Marketing</option>
          </select>

          <button
            type="submit"
            disabled={patchProject.isPending}
            style={{
              marginTop: 24, width: '100%', height: 44,
              background: 'var(--color-primary)', color: '#fff',
              borderRadius: 'var(--radius-sm)', fontWeight: 600, fontSize: 14,
              opacity: patchProject.isPending ? 0.6 : 1,
            }}
          >
            {patchProject.isPending ? '저장 중...' : '저장'}
          </button>
          {patchProject.error && (
            <p style={{ marginTop: 8, fontSize: 13, color: 'var(--color-danger-text)' }}>
              {(patchProject.error as Error).message}
            </p>
          )}
        </form>
      </Modal>
    </>
  )
}
